import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ChevronDown, Swords, BookOpen, Zap, History, ShoppingBag, FlaskConical, Backpack as BackpackIcon, RefreshCw, Sun, Moon, LogOut, Bell, Compass, Star, MessageSquare } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import NotificationBell from './NotificationBell';
import StudentFeedbackDialog from './StudentFeedbackDialog';

/**
 * Floating quick-menu pinned below the TrainerCard. Replaces the entire
 * top navbar — every action lives here now.
 */
const NAV_ACTIONS = [
  { key: 'missions', label: 'Missões',  path: '/student/missions', Icon: BookOpen,     color: 'bg-violet-500'  },
  { key: 'journeys', label: 'Jornadas', path: '/student/journeys', Icon: Compass,      color: 'bg-fuchsia-500' },
  { key: 'pokedex',  label: 'Pokédex',  path: '/student/pokedex',  Icon: Star,         color: 'bg-rose-500'    },
  { key: 'powers',   label: 'Poderes',  path: '/student/powers',   Icon: Zap,          color: 'bg-amber-500'   },
  { key: 'history',  label: 'Histórico',path: '/student/history',  Icon: History,      color: 'bg-emerald-500' },
  { key: 'shop',     label: 'Loja',     path: '/student/shop',     Icon: ShoppingBag,  color: 'bg-orange-500'  },
  { key: 'lab',      label: 'Lab',      path: '/student/lab',      Icon: FlaskConical, color: 'bg-sky-500'     },
];

export const QuickMenu = ({ onOpenBag }) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { setTheme, resolvedTheme, isDark } = useTheme();
  const [open, setOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const close = () => setOpen(false);
  const go = (path) => { close(); navigate(path); };

  const handleRefresh = () => { close(); window.dispatchEvent(new Event('dashboard-refresh')); };
  const handleDuel    = () => { close(); window.dispatchEvent(new Event('dashboard-open-duel')); };
  const handleLogout  = async () => {
    close();
    try { await logout(); navigate('/login'); } catch (e) { console.error(e); }
  };
  const handleTheme   = () => { setTheme(isDark ? 'light' : 'dark'); };

  return (
    <div
      data-testid="quick-menu"
      className="absolute top-[100px] sm:top-[110px] left-3 sm:left-4 z-50"
    >
      {/* Toggle button */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="quick-menu-toggle"
        className={`group flex items-center gap-2 px-3 py-1.5 rounded-full font-outfit font-black text-[11px] sm:text-xs tracking-widest uppercase text-slate-900 bg-amber-300 border-[3px] border-slate-900 shadow-[3px_3px_0_rgba(15,23,42,0.85)] hover:bg-amber-200 active:translate-y-0.5 active:shadow-[1px_1px_0_rgba(15,23,42,0.85)] transition-all ${open ? 'translate-y-0.5 shadow-[1px_1px_0_rgba(15,23,42,0.85)] bg-amber-200' : ''}`}
      >
        <Sparkles className="w-3.5 h-3.5" />
        Menu
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Expanded panel */}
      {open && (
        <div
          data-testid="quick-menu-panel"
          className="absolute top-[calc(100%+8px)] left-0 w-[280px] sm:w-[320px] bg-white/95 backdrop-blur-md border-[3px] border-slate-900 rounded-2xl shadow-[4px_4px_0_rgba(15,23,42,0.85)] p-3 animate-quickmenu"
        >
          {/* Section: Navigation */}
          <p className="font-outfit font-black text-[9px] tracking-widest uppercase text-slate-500 mb-2">Navegação</p>
          <div className="grid grid-cols-3 gap-2">
            {NAV_ACTIONS.map((a) => (
              <button
                key={a.key}
                type="button"
                onClick={() => go(a.path)}
                data-testid={`quick-action-${a.key}`}
                className={`flex flex-col items-center gap-1.5 p-2 rounded-xl ${a.color} text-white border-2 border-slate-900 shadow-[2px_2px_0_rgba(15,23,42,0.85)] hover:-translate-y-0.5 active:translate-y-0 active:shadow-[1px_1px_0_rgba(15,23,42,0.85)] transition-all`}
              >
                <a.Icon className="w-5 h-5" strokeWidth={2.5} />
                <span className="font-outfit font-black text-[10px] tracking-widest uppercase leading-none">{a.label}</span>
              </button>
            ))}
            <button
              type="button"
              onClick={() => { close(); onOpenBag && onOpenBag(); }}
              data-testid="quick-action-bag"
              className="flex flex-col items-center gap-1.5 p-2 rounded-xl bg-yellow-500 text-white border-2 border-slate-900 shadow-[2px_2px_0_rgba(15,23,42,0.85)] hover:-translate-y-0.5 active:translate-y-0 active:shadow-[1px_1px_0_rgba(15,23,42,0.85)] transition-all"
            >
              <BackpackIcon className="w-5 h-5" strokeWidth={2.5} />
              <span className="font-outfit font-black text-[10px] tracking-widest uppercase leading-none">Mochila</span>
            </button>
            <button
              type="button"
              onClick={handleDuel}
              data-testid="quick-action-duel"
              className="flex flex-col items-center gap-1.5 p-2 rounded-xl bg-rose-500 text-white border-2 border-slate-900 shadow-[2px_2px_0_rgba(15,23,42,0.85)] hover:-translate-y-0.5 active:translate-y-0 active:shadow-[1px_1px_0_rgba(15,23,42,0.85)] transition-all"
            >
              <Swords className="w-5 h-5" strokeWidth={2.5} />
              <span className="font-outfit font-black text-[10px] tracking-widest uppercase leading-none">Duelo</span>
            </button>
          </div>

          {/* Section: Utilities */}
          <p className="font-outfit font-black text-[9px] tracking-widest uppercase text-slate-500 mt-3 mb-2">Ações</p>
          <div className="grid grid-cols-5 gap-2">
            <button
              type="button"
              onClick={handleRefresh}
              data-testid="quick-action-refresh"
              className="flex flex-col items-center gap-1 p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-900 border-2 border-slate-300 transition-all"
              title="Atualizar dados"
            >
              <RefreshCw className="w-4 h-4" strokeWidth={2.5} />
              <span className="font-outfit font-black text-[9px] tracking-widest uppercase leading-none">Atualizar</span>
            </button>
            <div
              className="flex flex-col items-center gap-1 p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-900 border-2 border-slate-300 transition-all cursor-pointer"
              data-testid="quick-action-bell"
              title="Notificações"
            >
              {/* NotificationBell already renders its own button — drop it here. */}
              <Bell className="w-4 h-4 absolute opacity-0" /* visual placeholder */ aria-hidden="true" />
              <NotificationBell />
              <span className="font-outfit font-black text-[9px] tracking-widest uppercase leading-none">Avisos</span>
            </div>
            <button
              type="button"
              onClick={handleTheme}
              data-testid="quick-action-theme"
              className="flex flex-col items-center gap-1 p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-900 border-2 border-slate-300 transition-all"
              title={isDark ? 'Tema claro' : 'Tema escuro'}
            >
              {isDark ? <Sun className="w-4 h-4" strokeWidth={2.5} /> : <Moon className="w-4 h-4" strokeWidth={2.5} />}
              <span className="font-outfit font-black text-[9px] tracking-widest uppercase leading-none">Tema</span>
            </button>
            <button
              type="button"
              onClick={() => { close(); setFeedbackOpen(true); }}
              data-testid="quick-action-feedback"
              className="flex flex-col items-center gap-1 p-2 rounded-xl bg-fuchsia-100 hover:bg-fuchsia-200 text-fuchsia-800 border-2 border-fuchsia-300 transition-all"
              title="Enviar feedback ao professor"
            >
              <MessageSquare className="w-4 h-4" strokeWidth={2.5} />
              <span className="font-outfit font-black text-[9px] tracking-widest uppercase leading-none">Feedback</span>
            </button>
            <button
              type="button"
              onClick={handleLogout}
              data-testid="quick-action-logout"
              className="flex flex-col items-center gap-1 p-2 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-700 border-2 border-rose-300 transition-all"
              title="Sair"
            >
              <LogOut className="w-4 h-4" strokeWidth={2.5} />
              <span className="font-outfit font-black text-[9px] tracking-widest uppercase leading-none">Sair</span>
            </button>
          </div>
        </div>
      )}
      <StudentFeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </div>
  );
};

export default QuickMenu;
